'use client'
import type { FC, MouseEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { motion, AnimatePresence } from 'motion/react'
import { getIcon } from '@/utils'
import { Img } from '@/components'
import { mq, rgbAlpha, presets, vw } from '@/styles'
import { useMount } from '@/hooks'

type TypePosition = 'left' | 'right'
export interface iDrawer {
  children: ReactNode
  isActive: boolean
  hasHeader?: boolean
  hasToClose?: boolean
  zIndex?: number
  readonly close: () => void
  readonly position?: TypePosition
  readonly width?: number
  readonly title?: string
  readonly headerChildren?: ReactNode
  readonly dismissible?: boolean
  readonly showBackdrop?: boolean
  readonly bodyClassName?: string
}

export const Drawer: FC<iDrawer> = ({
  children,
  isActive,
  close,
  position = 'right',
  width = 400,
  title = '',
  hasHeader = true,
  zIndex = 1010,
  hasToClose = true,
  headerChildren,
  dismissible = true,
  showBackdrop = true,
  bodyClassName,
}) => {
  const isMounted = useMount()

  const onClick = (e: MouseEvent) => {
    if (dismissible && (e.target as HTMLElement).getAttribute('tabindex') === '-1') {
      close()
    }
  }

  return isMounted
    ? createPortal(
        <AnimatePresence>
          <DrawerStyled
            tabIndex={-1}
            initial={{ opacity: 0 }}
            animate={{ opacity: isActive ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.3 } }}
            aria-hidden={!isActive}
            onClick={onClick}
            $zIndex={zIndex}
            $isActive={isActive}
            $showBackdrop={showBackdrop}
          >
            <DrawerContentStyled
              role='dialog'
              aria-modal='true'
              $position={position}
              $width={width}
              $isActive={isActive}
              $bodyClassName={bodyClassName}
              initial={{ x: position === 'right' ? '100%' : '-100%' }}
              animate={{ x: isActive ? 0 : position === 'right' ? '100%' : '-100%' }}
              exit={{ x: position === 'right' ? '100%' : '-100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {hasHeader && (
                <header>
                  {headerChildren && headerChildren}
                  {title && <h2>{title}</h2>}
                  {hasToClose && (
                    <ButtonHeader type='button' className='btn--close' onClick={close}>
                      <Img media={getIcon('close')} fill={false} />
                    </ButtonHeader>
                  )}
                </header>
              )}
              <div className={`drawer__body ${bodyClassName || ''}`}>{children}</div>
            </DrawerContentStyled>
          </DrawerStyled>
        </AnimatePresence>,
        document.body
      )
    : null
}

const DrawerStyled = styled(motion.aside)<{
  $zIndex: iDrawer['zIndex']
  $isActive: iDrawer['isActive']
  $showBackdrop: iDrawer['showBackdrop']
}>`
  background: ${({ $showBackdrop }) => ($showBackdrop ? rgbAlpha('--color-black', 0.2) : 'transparent')};
  bottom: 0;
  display: ${({ $isActive }) => ($isActive ? 'flex' : 'none')};
  height: 100vh;
  height: 100dvh;
  left: 0;
  margin-right: 5px;
  max-width: 100vw;
  max-width: 100dvw;
  opacity: 0;
  outline: 0;
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  user-select: none;
  width: 100%;
  z-index: ${({ $zIndex }) => $zIndex};

  &[aria-hidden='false'] {
    opacity: 1;
    pointer-events: ${({ $showBackdrop }) => ($showBackdrop ? 'auto' : 'none')};
    user-select: auto;
  }
`

const DrawerContentStyled = styled(motion.div)<{
  $position: TypePosition
  $width: iDrawer['width']
  $isActive: iDrawer['isActive']
  $bodyClassName?: string
}>`
  background-color: var(--color-white);
  border-radius: 20px;
  box-shadow: ${({ $position }) => ($position === 'right' ? '-4px 0 20px rgba(0, 0, 0, .15)' : '4px 0 20px rgba(0, 0, 0, .15)')};
  display: flex;
  flex-direction: column;
  max-width: 90vw;
  height: calc(100vh - 20px);
  pointer-events: auto;
  position: fixed;
  ${({ $position }) => $position}: 0;
  top: 10px;
  width: ${({ $width }) => vw($width, 'mobile')};

  ${mq.greaterThan('tablet')} {
		height: calc(100vh - 60px);
		top: 80px;
    width: ${({ $width }) => vw($width, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    width: ${({ $width }) => $width + 'px'};
  }

  ${({ $isActive }) => !$isActive && css`
    user-select: none;
    pointer-events: none;

    > *,
    li,
    dd,
    button,
    a,
    form,
    fieldset,
    input,
    select,
    textarea,
    button,
    figure,
    picture,
    div {
      user-select: none;
      pointer-events: none;
    }
  `}

  > header {
    align-items: center;
    border-bottom: 1px solid ${rgbAlpha('--color-text', 0.1)};
    display: grid;
    grid-template-columns: 1fr 48px;
    height: 48px;
    justify-content: center;
    overflow-x: hidden;
    padding: 0 16px;
    position: relative;
    width: 100%;

    h2 {
      ${presets.font20}
      color: inherit;
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .drawer__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 0;
  }

  ${mq.lessThan('tablet')} {
    width: 100vw;
    max-width: 100vw;
  }
`

const ButtonHeader = styled.button`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  max-width: 48px;
  width: 48px;
  z-index: 2;

  img {
    max-height: 10px;
    max-width: 10px;
    width: 10px;
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }

  img {
    transition: transform 300ms ease-in-out;
  }
`
